import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "react-feather";
import { classList } from "../../lib/helpers";
import * as styles from "./PopoverMenu.module.scss";

export interface PopoverMenuProps {
  children?: React.ReactNode;
  triggerElement?: React.ReactNode;
  className?: React.ReactNode;
  currentLanguage?: string;
}

export interface PopoverMenuItemProps {
  title?: string;
  icon?: React.ReactNode;
  ref?: (el: HTMLDivElement) => HTMLDivElement;
  onClick?: () => void;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  children,
  triggerElement,
  className,
  currentLanguage,
}) => {
  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const popOverMenuRef = useRef<HTMLDivElement>(null);
  const languageOptionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const lastKeyPressed = useRef<string | null>(null);
  const switchLanguageBtnRef = useRef<HTMLButtonElement>(null);

  const childrenArray = React.Children.toArray(children);

  const handleMenuBarKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setMenuVisible((prev) => !prev);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % childrenArray.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev === 0 ? childrenArray.length - 1 : prev - 1
      );
    } else if (e.key === "Escape") {
      setMenuVisible(false);
      setFocusedIndex(-1);
    }
    lastKeyPressed.current = e.key;
  };

  useEffect(() => {
    if (focusedIndex >= 0 && languageOptionsRef.current[focusedIndex]) {
      languageOptionsRef.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (isMenuVisible) {
      popOverMenuRef.current?.focus();
    } else {
    }
  }, [isMenuVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popOverMenuRef.current &&
        !popOverMenuRef.current.contains(event.target as Node) &&
        !switchLanguageBtnRef.current?.contains(event.target as Node)
      ) {
        setMenuVisible(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popOverMenuRef, setMenuVisible, setFocusedIndex]);

  const classes = classList(styles.popoverMenu, className && className);

  const ariaLabelPopoverMenu = useMemo(() => {
    if (currentLanguage === "de") {
      return "Ausgewählte Sprache: Deutsch. Sprache ändern";
    }
    if (currentLanguage === "fr") {
      return "Langue sélectionnée : Français. Changer de langue.";
    }

    return "Selected language: English. Change language.";
  }, [currentLanguage]);

  return (
    <div
      className={classes}
      onKeyDown={handleMenuBarKeydown}
      onMouseOver={() => {
        setMenuVisible(true);
        setFocusedIndex(-1);
      }}
      onMouseLeave={() => {
        setMenuVisible(false);
        setFocusedIndex(-1);
      }}
      onBlur={() => {
        if (
          lastKeyPressed.current === "ArrowDown" ||
          lastKeyPressed.current === "ArrowUp"
        ) {
          return;
        } else {
          setMenuVisible(false);
          setFocusedIndex(-1);
        }
      }}
      // tabIndex={0}
    >
      <button
        aria-haspopup="true"
        ref={switchLanguageBtnRef}
        aria-label={ariaLabelPopoverMenu}
      >
        <div>{triggerElement}</div>
        <i>
          <ChevronDown aria-label="Chevron Down Icon" />
        </i>
      </button>
      <div
        className={styles.popoverMenu__popupContainer}
        style={{
          visibility: isMenuVisible ? "visible" : "hidden",
          opacity: isMenuVisible ? 1 : 0,
          transform: isMenuVisible ? "scale(1)" : "scale(0.5)",
          transition: "all 0.2s ease-in-out",
        }}
        role="menu"
      >
        <div
          className={styles.popoverMenu__popup}
          ref={popOverMenuRef}
          tabIndex={0}
        >
          {React.Children.map(children, (child, index) =>
            React.isValidElement(child)
              ? React.cloneElement(
                  child as React.ReactElement<
                    PopoverMenuItemProps & React.HTMLAttributes<HTMLDivElement>
                  >,
                  {
                    tabIndex: focusedIndex === index ? 0 : -1,
                    ref: (el: HTMLDivElement) =>
                      (languageOptionsRef.current[index] = el),
                    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter") {
                        child.props.onClick?.();
                        setMenuVisible(false);
                      }
                    },
                  }
                )
              : child
          )}
        </div>
      </div>
    </div>
  );
};

export const PopoverMenuItem = forwardRef<HTMLDivElement, PopoverMenuItemProps>(
  ({ title, icon, onClick, ...rest }, ref) => {
    return (
      <div
        className={styles.popoverMenuItem}
        role="menuitem"
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        <i>{icon}</i>
        <span>{title}</span>
      </div>
    );
  }
);

PopoverMenuItem.displayName = "PopoverMenuItem";
